import React from 'react';
import axios from 'axios';

import { CONFIG } from 'config';

/**
 * Stand-alone hook for the public /contratos counterparty flow.
 *
 * Deliberately does NOT use the app's `useApi` hook: that one is bound to the
 * logged-in user (attaches the user token, runs the refresh-token flow and
 * redirects to /login on 401). The counterparty here is NOT a logged-in user,
 * so we use an isolated axios instance and carry the short-lived
 * 'contract-validation' token returned by verify-pin.
 */
export function useContratos() {
    const [token, setToken] = React.useState(null);
    const [email, setEmail] = React.useState('');

    const api = React.useMemo(() => {
        const instance = axios.create({ baseURL: CONFIG.apiUrl });
        instance.interceptors.response.use(res => res.data);
        return instance;
    }, []);

    const authConfig = React.useCallback(
        () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
        [token]
    );

    const requestPin = React.useCallback(
        ({ email: e, locale }) =>
            api.post('/contractsvalidation/request-pin', {
                email: String(e || '')
                    .trim()
                    .toLowerCase(),
                locale,
            }),
        [api]
    );

    const verifyPin = React.useCallback(
        ({ email: e, pin }) => {
            const normalized = String(e || '')
                .trim()
                .toLowerCase();
            return api
                .post('/contractsvalidation/verify-pin', { email: normalized, pin })
                .then(res => {
                    if (res && res.token) {
                        setToken(res.token);
                        setEmail(normalized);
                    }
                    return res;
                });
        },
        [api]
    );

    const getContracts = React.useCallback(
        () => api.get('/contractsvalidation/contracts', authConfig()),
        [api, authConfig]
    );

    const validateContract = React.useCallback(
        ({ _id, status }) =>
            api.post(
                `/contractsvalidation/contracts/${_id}/validation`,
                { status },
                authConfig()
            ),
        [api, authConfig]
    );

    const validateEcosystemRelationship = React.useCallback(
        ({ _id, status }) =>
            api.post(
                `/contractsvalidation/ecosystem/${_id}/validation`,
                { status },
                authConfig()
            ),
        [api, authConfig]
    );

    const reset = React.useCallback(() => {
        setToken(null);
    }, []);

    return {
        token,
        email,
        requestPin,
        verifyPin,
        getContracts,
        validateContract,
        validateEcosystemRelationship,
        reset,
    };
}
export default useContratos;
